/**
 * Skills API - CRUD operations for skills and agent-skill attachments
 */

import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import { projectsApi } from './projects'
import type {
  Skill,
  SkillDetail,
  SkillCreate,
  SkillUpdate,
  SkillCloneRequest,
  SkillCloneResponse,
  SkillList,
  SkillVersion,
  SkillCategory,
  SkillType,
  SkillVisibility,
  AgentResolvedSkills,
} from './types'

export interface SkillListParams {
  page?: number
  page_size?: number
  search?: string
  project_id?: string
  visibility?: SkillVisibility
  category?: SkillCategory
  skill_type?: SkillType
  model_id?: string
  tags?: string
  include_marketplace?: boolean
  include_platform?: boolean
  source?: 'platform' | 'user'
  order_by?: 'name' | 'created_at' | 'updated_at' | 'usage_count' | 'rating' | 'category' | 'skill_type' | 'visibility'
  order_desc?: boolean
  omit_linked_for_project?: string
  only_approved_linked_for_project?: string
  include_project_membership_for?: string
  is_favourite?: boolean
  signal?: AbortSignal
}

export interface SkillListAllParams {
  search?: string
  category?: SkillCategory
  skill_type?: SkillType
  model_id?: string
  include_platform?: boolean
  source?: 'platform' | 'user'
  signal?: AbortSignal
}

export const skillsApi = {
  /**
   * List skills with filtering (paginated)
   */
  list: (params?: SkillListParams) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<SkillList>(`/skills${query}`, { signal })
  },

  /**
   * List all skills without pagination (for selectors/dropdowns)
   *
   * This endpoint is optimized for cases where all skills need to be loaded
   * at once, such as the Model Chat skill selector.
   */
  listAll: (params?: SkillListAllParams) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<SkillList>(`/skills/all${query}`, { signal })
  },

  /**
   * Get skill by ID
   */
  get: (id: string, options?: { include_content?: boolean; signal?: AbortSignal }) => {
    const { signal, ...queryParams } = options || {}
    const query = buildQueryString(queryParams)
    return request<SkillDetail>(`/skills/${id}${query}`, { signal })
  },

  /**
   * Get skill by slug
   */
  getBySlug: (slug: string, options?: { include_content?: boolean; signal?: AbortSignal }) => {
    const { signal, ...queryParams } = options || {}
    const query = buildQueryString(queryParams)
    return request<SkillDetail>(`/skills/by-slug/${slug}${query}`, { signal })
  },

  /**
   * Create a new skill
   */
  create: (data: SkillCreate, options?: { signal?: AbortSignal }) =>
    request<Skill>('/skills', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  /**
   * Update a skill
   */
  update: (
    id: string,
    data: SkillUpdate,
    options?: { create_version?: boolean; commit_message?: string; signal?: AbortSignal }
  ) => {
    const { signal, create_version, commit_message } = options || {}
    const query = buildQueryString({ create_version, commit_message })
    return request<Skill>(`/skills/${id}${query}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal,
    })
  },

  /**
   * Clone a skill into a target project (creates a new project-scoped skill).
   */
  clone: (id: string, data: SkillCloneRequest, options?: { signal?: AbortSignal }) =>
    request<SkillCloneResponse>(`/skills/${id}/clone`, {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  /**
   * Delete a skill
   */
  delete: (id: string, options?: { signal?: AbortSignal }) =>
    request<void>(`/skills/${id}`, {
      method: 'DELETE',
      signal: options?.signal,
    }),

  /**
   * Bulk delete skills
   */
  bulkDelete: (skillIds: string[], options?: { signal?: AbortSignal }) =>
    request<{ deleted: number; skipped: number; errors: string[] }>('/skills/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ skill_ids: skillIds }),
      signal: options?.signal,
    }),

  /**
   * Get skill content
   */
  getContent: (id: string, options?: { version_id?: string; signal?: AbortSignal }) => {
    const { signal, version_id } = options || {}
    const query = buildQueryString({ version_id })
    return request<{
      skill_id: string
      version_id: string | null
      content: string
      size_bytes: number
    }>(`/skills/${id}/content${query}`, { signal })
  },

  /**
   * List skill versions
   */
  getVersions: (
    id: string,
    options?: { page?: number; page_size?: number; signal?: AbortSignal }
  ) => {
    const { signal, ...queryParams } = options || {}
    const query = buildQueryString(queryParams)
    return request<{ items: SkillVersion[]; total: number; page: number; page_size: number }>(
      `/skills/${id}/versions${query}`,
      { signal }
    )
  },

  // =========================================================================
  // ACCESS CONTROL
  // =========================================================================

  getAccessStatistics: (id: string) => projectsApi.getSkillAccessStatistics(id),

  listAccessRequests: (
    id: string,
    params?: { status?: string; page?: number; page_size?: number }
  ) => projectsApi.listSkillAccessRequests(id, params),

  approveAccess: (id: string, requestId: string, action: 'approve' | 'reject') =>
    projectsApi.approveSkillAccess(id, requestId, action),

  revokeAccess: (id: string, requestId: string) => projectsApi.revokeSkillAccess(id, requestId),
}

/**
 * Agent Skills API
 *
 * NOTE: Skills are now stored in node data (node.data.skills[]) rather than
 * as separate agent-skill attachments. The only remaining endpoint is
 * getResolved which is used by Agentserve to fetch skills for execution.
 */
export const agentSkillsApi = {
  /**
   * Get resolved skills for an agent (for execution by Agentserve)
   *
   * This endpoint reads skills from the agent's graph_definition (node.data.skills[])
   * and returns them with full content ready for prompt injection.
   */
  getResolved: (agentId: string, options?: { signal?: AbortSignal }) =>
    request<AgentResolvedSkills>(`/agents/${agentId}/skills/resolved`, {
      signal: options?.signal,
    }),
}
